import React from 'react';
import { useParams, useHistory } from 'react-router-dom';
import Header from '../../components/StoreHeader';
import EventItems from '../../components/EventItems';
import StoreItems from '../../components/StoreItems';
import LoyaltyPath from '../../components/LoyaltyPath';
import LoyaltyHistory from '../../components/LoyaltyHistory';
import PurchaseHistory from '../../components/PurchaseHistory';
import Cart from '../cart';
import { api } from '../../services/api';
import { LangProvider, LangContext } from '../../contexts/LangContext';
import './Shop.css';

// ─── Assets visuais dos pacotes de evento (por índice) ────────────────────
const EVENT_VISUAL = [
  {
    image: 'img/chests-event/background-pack-featured-bau.png',
    chestImageSize: { width: '138px', height: '127px' },
    chestMarginRight: '-10px',
    chestOffsetY: '0px',
    bonusImage: 'img/np/01.png',
    bonusImageSize: { width: '130px', height: '124px' },
    bonusOffsetY: '-5px',
  },
  {
    image: 'img/chests-event/background-pack-featured-bau2.png',
    chestImageSize: { width: '138px', height: '127px' },
    chestMarginRight: '-10px',
    chestOffsetY: '0px',
    bonusImage: 'img/np/02.png',
    bonusImageSize: { width: '150px', height: '185px' },
    bonusOffsetY: '30px',
  },
  {
    image: 'img/chests-event/background-pack-featured-bau3.png',
    chestImageSize: { width: '138px', height: '127px' },
    chestMarginRight: '-10px',
    chestOffsetY: '0px',
    bonusImage: 'img/np/04.png',
    bonusImageSize: { width: '143px', height: '136px' },
    bonusOffsetY: '0px',
  },
  {
    image: 'img/chests-event/background-pack-featured-bau4.png',
    chestImageSize: { width: '128px', height: '117px' },
    chestMarginRight: '10px',
    chestOffsetY: '0px',
    bonusImage: 'img/np/05.png',
    bonusImageSize: { width: '108px', height: '108px' },
    bonusOffsetY: '-25px',
  },
];

const NP_VISUAL_SMALL = [
  { imageSize: 167, imageOffsetY: '-30px', infoOffsetY: '-40px', npBottomGap: '16px' },
  { imageSize: 198, imageOffsetY: '-45px', infoOffsetY: '-75px' },
  { imageSize: 167, imageOffsetY: '-45px', infoOffsetY: '-45px' },
  { imageSize: 198, imageOffsetY: '-45px', infoOffsetY: '-75px' },
];

const NP_VISUAL_LARGE = [
  { imageSize: 240, imageOffsetY: '-18px', infoOffsetY: '-70px' },
  { imageSize: 240, imageOffsetY: '-18px', infoOffsetY: '-70px' },
  { imageSize: 240, imageOffsetY: '-18px', infoOffsetY: '-70px' },
];

function mapApiToItems(apiData, lang, server, t) {
  const locale = lang === 'USD' ? 'en-US' : 'pt-BR';
  const currency = lang === 'USD' ? 'USD' : 'BRL';
  const fmt = (n) => n.toLocaleString(locale, { style: 'currency', currency });

  const hotItems = apiData.itemTable.filter(i => i.status !== 'normal');
  const normalItems = apiData.itemTable.filter(i => i.status === 'normal');

  const eventItems = hotItems.map((item, idx) => {
    const visual = EVENT_VISUAL[idx] || EVENT_VISUAL[EVENT_VISUAL.length - 1];
    return {
      sku: item.sku,
      name: item.name,
      status: item.status,
      npPrice: item.NP.toLocaleString(locale),
      brlPrice: fmt(item.priceReal),
      brlPriceValue: item.priceReal,
      gifts: item.gifts || [],
      availableuntil: item.availableuntil,
      leftTag: item.leftTag,
      limitText: item.availableuntil
        ? `${t.page.store.item.availableUntil} ${new Date(item.availableuntil).toLocaleDateString(locale)}`
        : null,
      ...visual,
    };
  });

  const threshold = server?.toLowerCase().includes('usd') ? 99.95 : 399.95;
  const smallItems = normalItems.filter(i => i.priceReal < threshold);
  const largeItems = normalItems.filter(i => i.priceReal >= threshold);

  const mapNpItem = (item, idx, visualArr) => {
    const visual = visualArr[idx] || {};
    const iconNum = item.icon ? item.icon.replace('.gif', '').replace('.png', '') : '01';
    const totalNP = item.NP + (item.NPBonus || 0);
    return {
      sku: item.sku,
      name: item.name,
      image: `img/np/${iconNum}.png`,
      npAmount: totalNP.toLocaleString(locale),
      baseNp: item.NPBonus > 0 ? item.NP.toLocaleString(locale) : null,
      bonusNp: item.NPBonus > 0 ? item.NPBonus.toLocaleString(locale) : null,
      bonusPercent: item.leftTag || null,
      brlPrice: fmt(item.priceReal),
      brlPriceValue: item.priceReal,
      gifts: item.gifts || [],
      size: 'small',
      ...visual,
    };
  };

  const storeItems = [
    ...smallItems.map((item, idx) => mapNpItem(item, idx, NP_VISUAL_SMALL)),
    ...largeItems.map((item, idx) => ({ ...mapNpItem(item, idx, NP_VISUAL_LARGE), size: 'large' })),
  ];

  const noticeLinks = apiData.noticeLink || [];
  const findLink = (tag) => {
    const byTag = noticeLinks.filter(l => l.tag === tag);
    if (!byTag.length) return null;
    return (byTag.find(l => l.lang === locale || l.lang === lang) || byTag[0])?.url || null;
  };

  return { eventItems, storeItems, npBalance: apiData.current_np || 0, promotionLink: findLink('promotion'), giftLink: findLink('gift') };
}

// ─── Inner (consumes context) ──────────────────────────────────────────────
function getServerForLang(currentServer, newLang) {
  const map = {
    globalbrl: { USD: 'globalusd', BRL: 'globalbrl' },
    globalusd: { USD: 'globalusd', BRL: 'globalbrl' },
    seasonbrl: { USD: 'seasonusd', BRL: 'seasonbrl' },
    seasonusd: { USD: 'seasonusd', BRL: 'seasonbrl' },
  };
  return map[currentServer]?.[newLang] ?? currentServer;
}

function ShopInner() {
  const { server } = useParams();
  const history = useHistory();
  const { lang, setLang, t } = React.useContext(LangContext);
  const startParams = JSON.parse(localStorage.getItem('startParams') || '{}');
  const username = startParams.username || '';
  const token = startParams.token || '';

  const [activePage, setActivePage] = React.useState('evento');
  const [lokiEventItems, setLokiEventItems] = React.useState([]);
  const [lokiStoreItems, setLokiStoreItems] = React.useState([]);
  const [lokiLoading, setLokiLoading] = React.useState(true);
  const [showLoyaltyPass, setShowLoyaltyPass] = React.useState(false);
  const [showCart, setShowCart] = React.useState(false);
  const [showPurchaseHistory, setShowPurchaseHistory] = React.useState(false);
  const [cartAction, setCartAction] = React.useState(null);
  const cartActionTimer = React.useRef(null);
  const [loyaltyData, setLoyaltyData] = React.useState(null);
  const [loyaltyLoading, setLoyaltyLoading] = React.useState(true);
  const [isLoyaltyUnderMaintenance, setIsLoyaltyUnderMaintenance] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [eventItems, setEventItems] = React.useState([]);
  const [storeItems, setStoreItems] = React.useState([]);
  const [npBalance, setNpBalance] = React.useState(0);
  const [historic, setHistoric] = React.useState([]);
  const [lokiHistoric, setLokiHistoric] = React.useState([]);
  const [cart, setCart] = React.useState({});
  const [isEligibleForGift, setIsEligibleForGift] = React.useState(false);
  const [purchasedSkus, setPurchasedSkus] = React.useState([]);
  const [promotionLink, setPromotionLink] = React.useState(null);
  const [giftLink, setGiftLink] = React.useState(null);

  React.useEffect(() => {
    if (!server || !username) return;
    // A rota passou a exigir o token de sessao da propria conta: sem ele devolvia o
    // historico de compra (purchasedSkus) de qualquer nick para qualquer um.
    api.get(`payment/verify-recharge-rewards-collected/${server}/${username}?token=${encodeURIComponent(token)}`)
      .then(response => {
        setIsEligibleForGift(response.data.eligible === true);
        setPurchasedSkus(response.data.purchasedSkus || []);
      })
      .catch((error) => {
        setIsEligibleForGift(false);
        setPurchasedSkus(error.response?.data?.purchasedSkus || []);
      });
  }, [server, username, token]);

  React.useEffect(() => {
    fetch('https://toalha.tapiocahut.com/service?service=loyalty_path')
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data?.status === 0) setIsLoyaltyUnderMaintenance(true); })
      .catch(() => { });
  }, []);

  const fetchLoyalty = React.useCallback(() => {
    if (!server || !username) {
      setLoyaltyLoading(false);
      return;
    }
    const backendServer = server.startsWith('season') ? 'season_prod' : 'global_prod';
    api.get(`payment/loyalty-path/${backendServer}/${username}?token=${encodeURIComponent(token)}`)
      .then(response => setLoyaltyData(response.data))
      .catch(err => console.error('Falha ao carregar loyalty path:', err))
      .finally(() => setLoyaltyLoading(false));
  }, [server, username, token]);

  React.useEffect(() => { fetchLoyalty(); }, [fetchLoyalty]);

  React.useEffect(() => {
    setLoading(true);
    setEventItems([]);
    setStoreItems([]);
    setActivePage('evento');
    api.get(`payment/cashdata?id=${encodeURIComponent(username)}&server=${server}&lang=${lang}&token=${encodeURIComponent(token)}`)
      .then(response => {
        const mapped = mapApiToItems(response.data, lang, server, t);
        setEventItems(mapped.eventItems);
        setStoreItems(mapped.storeItems);
        setNpBalance(mapped.npBalance);
        setPromotionLink(mapped.promotionLink);
        setGiftLink(mapped.giftLink);
        setHistoric((response.data.historic || []).map(r => ({ ...r, _server: 'Global' })));

        const catalog = [...mapped.eventItems, ...mapped.storeItems]
          .reduce((acc, item) => { acc[item.sku] = item; return acc; }, {});
        setCart(prev => {
          if (Object.keys(prev).length === 0) return prev;
          const next = {};
          for (const [sku, entry] of Object.entries(prev)) {
            next[sku] = catalog[sku] ? { ...entry, item: catalog[sku] } : entry;
          }
          return next;
        });

        setLoading(false);
        if (mapped.eventItems.length === 0) setActivePage('loja');
      })
      .catch(err => {
        console.error('Falha ao carregar dados da loja:', err);
        setLoading(false);
      });
  }, [server, username, lang]);

  React.useEffect(() => {
    if (!username) { setLokiLoading(false); return; }
    const seasonServer = lang === 'USD' ? 'seasonusd' : 'seasonbrl';
    setLokiLoading(true);
    api.get(`payment/cashdata?id=${encodeURIComponent(username)}&server=${seasonServer}&lang=${lang}&token=${encodeURIComponent(token)}`)
      .then(response => {
        console.log('[LOKI] raw response:', response.data);
        if (!response.data?.itemTable) {
          console.warn('[LOKI] resposta sem itemTable:', response.data);
          return;
        }
        const mapped = mapApiToItems(response.data, lang, seasonServer, t);
        console.log('[LOKI] mapped:', mapped);
        setLokiEventItems(mapped.eventItems);
        setLokiHistoric((response.data.historic || []).map(r => ({ ...r, _server: 'Season' })));

        const seasonCatalog = [...mapped.eventItems, ...mapped.storeItems]
          .reduce((acc, item) => { acc[item.sku] = item; return acc; }, {});
        setCart(prev => {
          if (Object.keys(prev).length === 0) return prev;
          const next = {};
          for (const [sku, entry] of Object.entries(prev)) {
            next[sku] = seasonCatalog[sku] ? { ...entry, item: seasonCatalog[sku] } : entry;
          }
          return next;
        });
      })
      .catch((err) => {
        console.error('[LOKI] erro ao carregar pacotes season:', err);
        setLokiEventItems([]);
        setLokiStoreItems([]);
      })
      .finally(() => setLokiLoading(false));
  }, [username, lang]);

  const triggerCartAction = (type) => {
    if (showCart) return;
    setCartAction({ type, id: Date.now() });
    clearTimeout(cartActionTimer.current);
    cartActionTimer.current = setTimeout(() => setCartAction(null), 2250);
  };

  const handleAddToCart = (item) => {
    setCart(prev => ({
      ...prev,
      [item.sku]: { item, qty: prev[item.sku] ? prev[item.sku].qty + 1 : 1 },
    }));
    triggerCartAction('added');
  };

  const handleRemoveFromCart = (sku) => {
    setCart(prev => { const next = { ...prev }; delete next[sku]; return next; });
    triggerCartAction('removed');
  };

  const handleQtyChange = (sku, qty) => {
    if (qty < 1) { handleRemoveFromCart(sku); return; }
    triggerCartAction(qty < (cart[sku]?.qty || 0) ? 'removed' : 'added');
    setCart(prev => ({ ...prev, [sku]: { ...prev[sku], qty } }));
  };

  const handleLangChange = (newLang) => {
    const newServer = getServerForLang(server, newLang);
    setLang(newLang);
    if (newServer !== server) {
      history.push(`/${newServer}`);
    }
  };

  const isLoading = loading || loyaltyLoading;
  const cartCount = Object.values(cart).reduce((sum, { qty }) => sum + qty, 0);
  const cartEntries = Object.values(cart);
  const cartTotalSpend = cartEntries.reduce(
    (sum, { item, qty }) => sum + (item.brlPriceValue ?? 0) * qty,
    0
  );

  const lokiSkus = React.useMemo(() => new Set([
    ...lokiEventItems.map(i => i.sku),
    ...lokiStoreItems.map(i => i.sku),
  ]), [lokiEventItems, lokiStoreItems]);

  const loyaltyCartSpend = cartEntries.reduce(
    (sum, { item, qty }) => lokiSkus.has(item.sku) ? sum : sum + (item.brlPriceValue ?? 0) * qty,
    0
  );

  return (
    <>
      <div className={`shop-page${cartCount > 0 && !showCart ? ' shop-page--with-pay-btn' : ''}${showCart ? ' shop-page--cart-open' : ''}`}>
        {showLoyaltyPass && <LoyaltyHistory loyaltyData={loyaltyData} onClose={() => setShowLoyaltyPass(false)} />}
        {showPurchaseHistory && <PurchaseHistory historic={[...historic, ...lokiHistoric]} onClose={() => setShowPurchaseHistory(false)} />}

        <Header
          activePage={activePage}
          showLoki={!lokiLoading && (lokiEventItems.length > 0 || lokiStoreItems.length > 0)}
          tabsDisabled={showCart}
          onNavigate={(page) => { setActivePage(page); setShowCart(false); }}
          npBalance={npBalance}
          cartCount={cartCount}
          cartEntries={cartEntries}
          cartAction={cartAction}
          onLoyaltyPass={!isLoyaltyUnderMaintenance ? () => setShowLoyaltyPass(true) : undefined}
          onHistorico={() => setShowPurchaseHistory(true)}
          onCart={() => showCart ? setShowCart(false) : setShowCart(true)}
          lang={lang}
          onLangChange={handleLangChange}
        />

        <div className="shop-content">
          {isLoading ? (
            <div className="shop-loading">{t.component.shop.loading}</div>
          ) : showCart ? (
            <Cart
              cart={cart}
              server={server}
              lokiSkus={lokiSkus}
              onClose={() => setShowCart(false)}
              onRemove={handleRemoveFromCart}
              onQtyChange={handleQtyChange}
            />
          ) : activePage === 'evento' ? (
            <EventItems items={eventItems} server={server} onAddToCart={handleAddToCart} cart={cart} onQtyChange={handleQtyChange} onRemove={handleRemoveFromCart} onCart={() => setShowCart(true)} isEligibleForGift={isEligibleForGift} purchasedSkus={purchasedSkus} promotionLink={promotionLink} giftLink={giftLink} />
          ) : activePage === 'loja' ? (
            <StoreItems items={storeItems} server={server} onAddToCart={handleAddToCart} cart={cart} onQtyChange={handleQtyChange} onRemove={handleRemoveFromCart} onCart={() => setShowCart(true)} />
          ) : lokiLoading ? (
            <div className="shop-loading">{t.component.shop.loading}</div>
          ) : (
            <>
              <EventItems items={lokiEventItems} server={server} onAddToCart={handleAddToCart} cart={cart} onQtyChange={handleQtyChange} onRemove={handleRemoveFromCart} onCart={() => setShowCart(true)} isEligibleForGift={false} purchasedSkus={[]} promotionLink={null} giftLink={null} variant='loki' />
              <StoreItems items={lokiStoreItems} server={server} onAddToCart={handleAddToCart} cart={cart} onQtyChange={handleQtyChange} onRemove={handleRemoveFromCart} onCart={() => setShowCart(true)} />
            </>
          )}
          {!isLoading && !showCart && (
            <>
              <div className={`finalizar-wrapper finalizar-wrapper--inline${cartCount > 0 ? '' : ' finalizar-wrapper--hidden'}`}>
                <button
                  className={`finalizar-shop-btn${cartCount === 0 ? ' finalizar-shop-btn--disabled' : ''}`}
                  disabled={cartCount === 0}
                  onClick={() => cartCount > 0 && setShowCart(true)}
                >
                  {cartCount === 0 ? t.component.shop.selectPackage : t.component.shop.goToPayment}
                </button>
              </div>
              <div className="divider-wrapper">
                <img src="img/divider.png" alt="" className="divider-img" />
              </div>
            </>
          )}
        </div>
        {!isLoading && !showCart && !isLoyaltyUnderMaintenance && activePage !== 'loki' && (
          <div className="loyalty-wrapper">
            <LoyaltyPath loyaltyData={loyaltyData} cartTotalSpend={loyaltyCartSpend} server={server} token={token} username={username} onClaimSuccess={fetchLoyalty} />
          </div>
        )}
      </div>
      {!isLoading && !showCart && (
        <div className={`finalizar-wrapper finalizar-wrapper--fixed${cartCount > 0 ? '' : ' finalizar-wrapper--hidden'}`}>
          <button
            className={`finalizar-shop-btn${cartCount === 0 ? ' finalizar-shop-btn--disabled' : ''}`}
            disabled={cartCount === 0}
            onClick={() => cartCount > 0 && setShowCart(true)}
          >
            {cartCount === 0 ? t.component.shop.selectPackage : t.component.shop.goToPayment}
          </button>
        </div>
      )}
    </>
  );
}

export default function Shop() {
  return (
    <LangProvider>
      <ShopInner />
    </LangProvider>
  );
}
